import { useState, useCallback } from "react";
import { API_BASE_URL } from "@/api/client";
import { useAuth } from "../context/AuthContext";

/**
 * Stream event types from the guided chat API:
 * - token: Raw streaming tokens for typing indicator
 * - message: Structured message object (final content)
 * - done: Stream complete
 * - error: Error occurred
 */
interface TokenEvent {
  type: "token";
  content: string;
}

interface MessageEvent {
  type: "message";
  data: {
    type: string; // "text", "image", etc.
    text_content?: string;
    // ... other message fields
  };
}

interface DoneEvent {
  type: "done";
}

interface ErrorEvent {
  type: "error";
  content: string;
}

type StreamEvent = TokenEvent | MessageEvent | DoneEvent | ErrorEvent;

export function useStreamingResponse(guidedChatId: number) {
  const { token } = useAuth();
  const [streamedContent, setStreamedContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = useCallback(
    async (content: string): Promise<string> => {
      setIsStreaming(true);
      setStreamedContent("");
      setError(null);

      // Track streaming tokens for typing indicator
      let tokenContent = "";
      // Collect structured messages for final response
      const messages: string[] = [];

      try {
        const response = await fetch(
          `${API_BASE_URL}/web/guided-chats/${guidedChatId}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ content }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No response body");
        }

        const decoder = new TextDecoder();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split("\n");

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6)) as StreamEvent;

                if (data.type === "error") {
                  setError(data.content || "Unknown error");
                  break;
                }

                if (data.type === "token") {
                  // Accumulate tokens for streaming display
                  tokenContent += data.content;
                  setStreamedContent(tokenContent);
                }

                if (data.type === "message") {
                  // Collect structured message content
                  if (data.data.text_content) {
                    messages.push(data.data.text_content);
                  }
                }

                if (data.type === "done") {
                  // Stream complete - use structured messages as final response
                  break;
                }
              } catch {
                // Skip invalid JSON lines
              }
            }
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsStreaming(false);
      }

      // Return combined message content (or fallback to streamed tokens)
      return messages.length > 0 ? messages.join("\n\n") : tokenContent;
    },
    [guidedChatId, token]
  );

  const reset = useCallback(() => {
    setStreamedContent("");
    setError(null);
  }, []);

  return {
    streamedContent,
    isStreaming,
    error,
    sendMessage,
    reset,
  };
}
